<template>
  <div>
    <el-dialog v-if="dialogVisible"
      :title="editingCampaign ? 'Edit Campaign' : 'Create new campaign'"
      :visible.sync="dialogVisible"
      top="5vh">
      <campaign-form
        v-if="showCampaignForm"
        :channels="channelList"
        :selectedChannels="selectedChannels"
        :loading="mutationLoading"
        :campaignToEdit="campaignData"
        :isEditing="editingCampaign"
        :plugins="plugins"
        @onSelectedChannelsChanged="handleSelectedChannelsChanged"
        @onRequestTwitterAuth="handleRequestTwitterAuth"
        @onRequestFacebookAuth="handleRequestFacebookAuth"
        @onRequestInstagramAuth="handleRequestInstagramAuth"
        @onRequestEmailChannelCreate="handleRequestEmailChannelCreate"
        @onRequestWhatsappChannelCreate="handleRequestWhatsappChannelCreate"
        @onFormCancel="handleFormCancel"
        @onFormSave="handleFormSave"
      />

      <div v-if="!showCampaignForm">
        <loader message="Loading campaign detail" />
      </div>

      <facebook-create-dialog
        :me="me"
        :isVisible="showFacebookCreateDialog"
        @onFacebookChannelAdded="refetchChannelList"
        @onVisibleChanged="handleFacebookVisibilityChange"
      />

      <email-channel-create-dialog
        :me="me"
        :isVisible="showEmailCreateDialog"
        @onEmailChannelAdded="refetchChannelList"
        @onVisibleChanged="handleEmailDialogVisibilityChange"
      />

      <whatsapp-web-channel-create-dialog
        :isVisible="showWhatsappCreateDialog"
        @onWhatsappChannelAdded="refetchChannelList"
        @onVisibleChanged="handleWhatsappDialogVisibilityChange"
      />

      <instagram-create-dialog
        :me="me"
        :isVisible="showInstagramCreateDialog"
        @onInstagramChannelAdded="refetchChannelList"
        @onVisibleChanged="handleInstagramVisibilityChange"
      />

    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import gql from 'graphql-tag';
import CampaignForm from '@/components/campaign/CampaignForm';
import FacebookCreateDialog from '@/components/channel/facebook/FacebookCreateDialog';
import InstagramCreateDialog from '@/components/channel/instagram/InstagramCreateDialog';
import EmailChannelCreateDialog from '@/components/channel/email/EmailChannelCreateDialog';
import WhatsappWebChannelCreateDialog from '@/components/channel/whatsappweb/WhatsappWebChannelCreateDialog';
import Loader from '@/components/common/Loader';

import * as channelTypes from '@/store/modules/channel/mutation-types';

import createCompetitionCampaignMutation from '@/graphql/createCompetitionCampaign.gql';
import createPollCampaignMutation from '@/graphql/createPollCampaign.gql';
import createMessageBoardCampaignMutation from '@/graphql/createMessageBoardCampaign.gql';
import updateCompetitionCampaignMutation from '@/graphql/updateCompetitionCampaign.gql';
import updatePollCampaignMutation from '@/graphql/updatePollCampaign.gql';
import updateMessageBoardCampaignMutation from '@/graphql/updateMessageBoardCampaign.gql';
import campaignEditQuery from '@/graphql/campaignEditQuery.gql';

export default {
  name: 'CampaignManage',

  components: {
    CampaignForm,
    FacebookCreateDialog,
    InstagramCreateDialog,
    EmailChannelCreateDialog,
    WhatsappWebChannelCreateDialog,
    Loader,
  },

  data: () => ({
    channelList: [],
    showFacebookCreateDialog: false,
    showInstagramCreateDialog: false,
    showEmailCreateDialog: false,
    showWhatsappCreateDialog: false,
    showCampaignForm: false,
    campaignData: null,
    mutationLoading: false,
  }),

  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },

    addingInstagramChannel: {
      type: Boolean,
      required: false,
    },

    addingTwitterChannel: {
      type: Boolean,
      required: false,
    },

    campaignToEdit: {
      type: String,
      required: false,
    },

    editingCampaign: {
      type: Boolean,
      default: false,
    },

    me: {
      type: Object,
      required: true,
    },
  },

  apollo: {
    channelList: {
      query: gql`
        {
          channelList(live: true) {
            id
            handle
            channelTypeTitle
          }
        }
      `,
    },
  },

  computed: {
    ...mapState('channel', ['selectedChannels']),
    ...mapState('plugin', ['plugins']),

    dialogVisible: {
      get() {
        return this.isVisible;
      },
      set(visibility) {
        this.$emit('onVisibleChanged', visibility);
      },
    },
  },

  mounted() {
    this.refetchChannelList();
    if (this.editingCampaign) {
      this.showCampaignForm = false;

      this.$apollo
        .query({
          query: campaignEditQuery,
          variables: { campaignId: this.campaignToEdit },
        })
        .then(result => {
          this.campaignData = result.data.campaign;
          this.showCampaignForm = true;
        })
        .catch(error => {
          this.$notify({
            title: 'Error!',
            message: `Unable to load campaign detail: ${this.friendlyError(error)}.`,
            type: 'error',
          });
        });
    } else {
      this.showCampaignForm = true;
    }
  },

  methods: {
    /**
     * Creates a campaign of type competition.
     *
     * Competition campaigns include advertiser data.
     *
     * @param campaignData
     * @param advertiserData
     * @param channelData
     * @param replyMessageData
     * @param isEditing
     * @returns {*|void}
     */
    submitCompetitionCampaign(campaignData, channelData, replyMessageData, isEditing,
      replyMessageCredentialsData) {
      this.mutationLoading = true;

      this.$apollo
        .mutate({
          mutation: isEditing
            ? updateCompetitionCampaignMutation
            : createCompetitionCampaignMutation,
          variables: {
            campaignId: isEditing ? campaignData.id : null,
            campaignTitle: campaignData.title,
            campaignStartDate: campaignData.startDate,
            campaignEndDate: campaignData.endDate,
            campaignType: campaignData.campaignType,
            channelKeywords: channelData.keywords.map(c => c.trim()),
            channelIds: channelData.channels.map(c => parseInt(c, 10)),
            perpetual: campaignData.perpetual,
            replyMessage: replyMessageData.text,
            encryptOriginator: replyMessageData.encryptOriginator,
            smsReplyUsername: replyMessageCredentialsData.username,
            smsReplyPassword: replyMessageCredentialsData.password,
            winningDisqualificationDuration: campaignData.winningDisqualificationDuration,
          },
        })
        .then(() => {
          this.mutationLoading = false;
          this.$emit('onSaved');
          this.$notify({
            title: 'Success!',
            message: `Your competition was ${isEditing ? 'updated' : 'created'} successfully.`,
            type: 'success',
          });
        })
        .catch(error => {
          this.mutationLoading = false;
          this.$notify({
            title: 'Error!',
            message: `Unable to ${
              isEditing ? 'update' : 'create'
            } your competition: ${this.friendlyError(error)}.`,
            type: 'error',
          });
        });
    },

    /**
     * Creates a campaign of type Poll.
     *
     * @param campaignData
     * @param channelData
     * @param pollData
     * @param replyMessageData
     * @param isEditing
     * @returns {*|void}
     */
    submitPollCampaign(campaignData, channelData, pollData, replyMessageData, isEditing,
      replyMessageCredentialsData) {
      this.mutationLoading = true;

      this.$apollo
        .mutate({
          mutation: isEditing ? updatePollCampaignMutation : createPollCampaignMutation,
          variables: {
            campaignId: isEditing ? campaignData.id : null,
            campaignTitle: campaignData.title,
            campaignStartDate: campaignData.startDate,
            campaignEndDate: campaignData.endDate,
            campaignType: campaignData.campaignType,
            perpetual: campaignData.perpetual,
            channelKeywords: channelData.keywords.map(c => c.trim()),
            channelIds: channelData.channels.map(c => parseInt(c, 10)),
            pollQuestion: pollData.question,
            pollChoices: pollData.choices,
            replyMessage: replyMessageData.text,
            encryptOriginator: replyMessageData.encryptOriginator,
            smsReplyUsername: replyMessageCredentialsData.username,
            smsReplyPassword: replyMessageCredentialsData.password,
            winningDisqualificationDuration: campaignData.winningDisqualificationDuration,
          },
        })
        .then(() => {
          this.mutationLoading = false;
          this.$emit('onSaved');
          this.$notify({
            title: 'Success!',
            message: `Your Poll was ${isEditing ? 'updated' : 'created'} successfully.`,
            type: 'success',
          });
        })
        .catch(error => {
          this.mutationLoading = false;
          this.$notify({
            title: 'Error!',
            message: `Unable to ${isEditing ? 'update' : 'create'} your Poll: ${this.friendlyError(
              error,
            )}.`,
            type: 'error',
          });
        });
    },

    /**
     * Creates a campaign of type message board.
     *
     * @param campaignData
     * @param channelData
     * @param replyMessageData
     * @param isEditing
     * @returns {*|void}
     */
    submitMessageBoardCampaign(campaignData, channelData, replyMessageData, isEditing,
      replyMessageCredentialsData) {
      this.mutationLoading = true;
      this.$apollo
        .mutate({
          mutation: isEditing
            ? updateMessageBoardCampaignMutation
            : createMessageBoardCampaignMutation,
          variables: {
            campaignId: isEditing ? campaignData.id : null,
            campaignTitle: campaignData.title,
            campaignStartDate: campaignData.startDate,
            campaignEndDate: campaignData.endDate,
            campaignType: campaignData.campaignType,
            perpetual: campaignData.perpetual,
            channelKeywords: channelData.keywords.map(c => c.trim()),
            channelIds: channelData.channels.map(c => parseInt(c, 10)),
            replyMessage: replyMessageData.text,
            encryptOriginator: replyMessageData.encryptOriginator,
            smsReplyUsername: replyMessageCredentialsData.username,
            smsReplyPassword: replyMessageCredentialsData.password,
            winningDisqualificationDuration: campaignData.winningDisqualificationDuration,
          },
        })
        .then(() => {
          this.mutationLoading = false;
          this.$emit('onSaved');
          this.$notify({
            title: 'Success!',
            message: `Your message board was ${isEditing ? 'updated' : 'created'} successfully.`,
            type: 'success',
          });
        })
        .catch(error => {
          this.mutationLoading = false;
          this.$notify({
            title: 'Error!',
            message: `Unable to ${
              isEditing ? 'update' : 'create'
            } your message board: ${this.friendlyError(error)}.`,
            type: 'error',
          });
        });
    },

    /**
     * Produce a friendly error message to display to a user.
     *
     * @param error: String
     * @returns String
     */
    friendlyError(error) {
      const errors = {
        'GraphQL error: Cannot create duplicate rows.':
          'A campaign with this keyword and channel combination already exists.',
      };
      if (Object.keys(errors).indexOf(error.message) !== -1) {
        return errors[error.message];
      }

      // We didn't get the error message we expected, lets just make it
      // slightly friendlier.
      return error.message.replace('GraphQL error: ', '');
    },

    handleSelectedChannelsChanged(selectedChannels) {
      this.$store.commit(`channel/${channelTypes.SET_SELECTED_CHANNELS}`, selectedChannels);
    },

    handleRequestTwitterAuth() {
      this.$emit('onRequestTwitterAuth');
    },

    handleRequestFacebookAuth() {
      this.showFacebookCreateDialog = true;
    },

    handleFacebookVisibilityChange(value) {
      this.showFacebookCreateDialog = value;
    },

    handleRequestInstagramAuth() {
      this.showInstagramCreateDialog = true;
    },

    handleInstagramVisibilityChange(value) {
      this.showInstagramCreateDialog = value;
    },

    handleRequestEmailChannelCreate() {
      this.showEmailCreateDialog = true;
    },

    handleEmailDialogVisibilityChange(value) {
      this.showEmailCreateDialog = value;
    },

    handleRequestWhatsappChannelCreate() {
      this.showWhatsappCreateDialog = true;
    },

    handleWhatsappDialogVisibilityChange(value) {
      this.showWhatsappCreateDialog = value;
    },

    handleFormCancel() {
      this.$emit('onVisibleChanged', false);
    },

    /**
     * Handle the saving of a campaign from data received back from
     * the campaign form.
     *
     * @param campaignData
     * @param channelData
     * @param pollData
     * @param replyMessageData
     * @param isEditing
     * @returns {*|void}
     */
    handleFormSave({ campaignData, channelData, pollData, replyMessageData, isEditing,
    replyMessageCredentialsData }) {
      if (campaignData.campaignType === 'COMPETITION') {
        return this.submitCompetitionCampaign(
          campaignData,
          channelData,
          replyMessageData,
          isEditing,
          replyMessageCredentialsData,
        );
      }

      if (campaignData.campaignType === 'POLL') {
        return this.submitPollCampaign(
          campaignData,
          channelData,
          pollData,
          replyMessageData,
          isEditing,
          replyMessageCredentialsData,
        );
      }

      return this.submitMessageBoardCampaign(
        campaignData,
        channelData,
        replyMessageData,
        isEditing,
        replyMessageCredentialsData,
      );
    },

    refetchChannelList() {
      this.$apollo.queries.channelList.refetch();
    },
  },

  watch: {
    addingInstagramChannel(adding, oldVal) {
      if (!adding && oldVal) {
        this.refetchChannelList();
      }
    },

    addingTwitterChannel(adding, oldVal) {
      if (!adding && oldVal) {
        this.refetchChannelList();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.loader-text {
  text-align: center;
  font-size: 1.25em;
}
</style>



// WEBPACK FOOTER //
// CampaignManage.vue?b0c3ee80